import Enumerable from "linq";
import store from "../../../../store";
import { IWebApplicationMessageCommunicatable, WebApplicationExecutePatterns, WebApplicationInformation } from "../../../../domain/localhost";
import { ExecuteRequest, ExternalScriptExecuteResult, ScriptLinkItem } from "../../../../domain/localhost/items";
import { IConfiguration } from "../../../../domain/configuration";
import { CommunicatorBase } from "./CommunicatorBase";
import { ArgumentExtractor } from "./ArgumentExtractor";

interface IHisDataPickerMessage {
  sourceApplication: string;
  command: string;
  value: {
    id: number,
    freeText: string,
  };
}

class ValueInformation {
  constructor(public objectName: string, public value: string) {
  }
}

interface IHisDataPickerCommunicatorConfiguration {
  uri: string;
}

export class HisDataPickerCommunicator extends CommunicatorBase implements IWebApplicationMessageCommunicatable {
  private readonly _idObjectNameParameterKey: string = "-idObjectName";
  private readonly _freeTextObjectNameParameterKey: string = "-exObjectName"; // 現行のHisDataPickerと引数を合わせている
  
  constructor(request: ExecuteRequest, registerResult: (result: ExternalScriptExecuteResult) =>  Promise<void>, getConfiguration: (key: string) => Promise<any>) {
    super(request, registerResult, getConfiguration);
  }
  private _configuration: IHisDataPickerCommunicatorConfiguration;

  public isProcessible(): boolean {
    if (!this._request || !this._request.exePath) { return false; }
    const configuration = (store.state.configuration as IConfiguration).behavior.localhost.externalScript.webApplication.hisDataPicker;
    return configuration.isEnable && (new RegExp(configuration.pathExpression, "i")).test(this._request.exePath);
  }

  async initialize(): Promise<void> {
    const configuration = await this._getConfiguration("HisDataPicker");
    console.debug(configuration);
    if (!configuration || !configuration.uri) { throw new Error("設定の取得に失敗しました。"); }
    this._configuration = configuration;
  }

  async getApplicationInformation(communicatorMessage: string): Promise<WebApplicationInformation> {
    const cacheSuppressor = new Date().getTime();
    const url = this._configuration.uri.replace(/\/$/, "") + "/editor?patientNo=" + this._request.patientId + "&cacheSuppressor=" + cacheSuppressor;
    return new WebApplicationInformation(url, WebApplicationExecutePatterns.Iframe, null, null);
  }

  analyzeMessage(message: IHisDataPickerMessage, postedData: any): void {
    if (!message || message.sourceApplication !== "WebHisDataPicker") { return; }

    if (message.command === "request-his-data") {
      const responseMessage = this.createResponseMessage();
      this.postMessage(responseMessage);
    }
    if (message.command === "select-his-data") {
      const result = this.createExecuteResult(message);
      this._registerResult(result).then(() => this.onComplete());
    }
    if (message.command === "cancel") {
      const result = this.createCancelResult();
      this._registerResult(result).then(() => this.onComplete());
    }
  }

  private createResponseMessage(): IHisDataPickerMessage {
    const argument = new ArgumentExtractor(this._request.arguments);
    const idObjectName = argument.getValue(this._idObjectNameParameterKey);
    const freeTextObjectName = argument.getValue(this._freeTextObjectNameParameterKey);

    return {
      sourceApplication: "documaker",
      command: "set-his-data",
      value: {
        id: this.toNumber(this.getCurrentValue(idObjectName)),
        freeText: this.getCurrentValue(freeTextObjectName),
      },
    };
  }

  private getCurrentValue(objectName: string): string {
    const found = Enumerable.from(this._request.scriptLinkItems).firstOrDefault(i => objectName === i.name);
    return !!found ? found.value : "";
  }

  private toNumber(value: string): number {
    if (!value) { return null; }
    const parsed = parseInt(value, 10);
    return isNaN(parsed)
      ? null
      : parsed;
  }

  createCancelResult(): ExternalScriptExecuteResult {
    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.isCancel = true;
    result.values = [];
    result.scriptLinkResults = [];
    return result;
}

  private createExecuteResult(message: IHisDataPickerMessage): ExternalScriptExecuteResult {
    if (!message.value) {
      return this.createCancelResult();
    }
    const argument = new ArgumentExtractor(this._request.arguments);
    const idObjectName = argument.getValue(this._idObjectNameParameterKey);
    const freeTextObjectName = argument.getValue(this._freeTextObjectNameParameterKey);
    const values = Enumerable.from([])
      .concat(this.createValues(message.value.id == null ? "" : message.value.id + "", idObjectName))
      .concat(this.createValues(message.value.freeText, freeTextObjectName))
      .toArray();

    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.values = [];
    result.scriptLinkResults = this.toScriptLinkResults(this._request.scriptLinkItems, values);
    return result;
  }

  private createValues(value: string, objectName: string): ValueInformation[] {
    if (!objectName) { return []; }
    return Enumerable.from(this._request.scriptLinkItems)
      .where(i => objectName === i.name)
      .select(i => new ValueInformation(i.name, value))
      .toArray();
  }

  private toScriptLinkResults(scriptLinkItems: ScriptLinkItem[], values: ValueInformation[]): ScriptLinkItem[] {
    if (!values) { return []; }
    return Enumerable.from(scriptLinkItems)
      .select(i => this.createResultItem(i, Enumerable.from(values).firstOrDefault(v => v.objectName === i.name)))
      .where(i => !!i)
      .toArray();
  }

  private createResultItem(source: ScriptLinkItem, value: ValueInformation): ScriptLinkItem {
    if (!value) { return null; }
    const cloned = source.clone();
    cloned.value = value.value ? value.value : "";
    return cloned;
  }
}
